import React, { useState, useEffect } from 'react'
import { styled } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'
import * as api from '@owl-nest/api-client/next'

import { type AuthenticatedUser } from '@owl-nest/api-client/latest'
import { EULA_UPDATED_AT } from '@owl-nest/config'

type EulaModalProps = {
  user: AuthenticatedUser
  onValid?: () => void
  refresh?: () => void
}

function useEulaModal(user: AuthenticatedUser, cguDate: string | undefined, onValid: () => void, refresh: () => void) {
  const [isEulaOpen, setEulaOpen] = useState(false)

  return { isEulaOpen, shouldDisplayEula, acceptEula }

  async function acceptEula() {
    const response = await api.client('POST /users/:id/accepteula', {
      urlParams: { id: String(user.id) },
    })

    if (response.isRight()) {
      refresh()
      setEulaOpen(false)
      onValid()
    }
  }

  async function shouldDisplayEula() {
    if (!cguDate || !user.eula_accepted_at) {
      return
    }

    const userEulaTimestamp = user.eula_accepted_at ? new Date(user.eula_accepted_at).getTime() : 0
    const cguTimestamp = new Date(cguDate).getTime()

    if (cguTimestamp > userEulaTimestamp) {
      setEulaOpen(true)
    } else {
      onValid()
    }
  }
}

export function EulaModal({ user, onValid = () => {}, refresh = () => {} }: EulaModalProps) {
  const { isEulaOpen, shouldDisplayEula, acceptEula } = useEulaModal(user, EULA_UPDATED_AT, onValid, refresh)

  useEffect(() => {
    shouldDisplayEula()
  }, [user])

  if (!isEulaOpen) {
    return null
  }

  return (
    <Modal open={isEulaOpen} closable={false}>
      <plume.styles.heading.M>{t('Update of our Terms of Use')}</plume.styles.heading.M>
      <plume.styles.copy.M.light>
        {t('To continue using our services, please read the changes to our terms and conditions 👇')}
      </plume.styles.copy.M.light>

      <iframe title="title" src="/pages/about/terms/iframe" />

      <plume.Button onClick={acceptEula}>{t('Accept')}</plume.Button>
    </Modal>
  )
}

const Modal = styled(plume.Modal)`
  iframe {
    border: 0;
    height: 100%;
    width: 100%;
    padding-bottom: 16px;
  }

  ${plume.styles.modal.Wrapper} {
    max-height: 100%;
    max-width: 780px;
  }

  ${plume.styles.modal.Body} {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 780px;
    padding-left: 0;
    padding-right: 0;
  }

  ${plume.styles.copy.M.light}, ${plume.styles.heading.M} {
    padding-left: 24px;
    padding-right: 24px;
  }

  ${plume.styles.heading.M} {
    padding-bottom: 8px;
  }

  ${plume.styles.modal.Actions} {
    height: 60px;
    margin-bottom: 20px;
    padding: 0;
  }

  @media screen and (${plume.BREAKPOINTS.TABLET}) {
    ${plume.styles.modal.Wrapper} {
      box-sizing: border-box;
      max-height: 90vh;
    }

    ${plume.styles.modal.Body} {
      height: 80vh;
      padding-left: 6px;
      padding-right: 6px;
    }
  }
`
